import React, { memo, useMemo } from 'react';
import { CONNECTION_TYPES } from './hooks/useArchitectureState';
import { getNodeAnchor } from './ArchitectureNode';

/**
 * Generate SVG path with orthogonal routing and rounded corners
 * Creates right-angle paths with smooth rounded corners at each bend
 */
function generatePath(source, target) {
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const cornerRadius = 8; // Radius for rounded corners

  // Minimum horizontal distance before first turn
  const minHorizontal = 30;
  const midX = source.x + Math.max(dx / 2, minHorizontal);

  // If source and target are roughly at same Y, just draw a straight line
  if (Math.abs(dy) < 5) {
    return `M ${source.x} ${source.y} L ${target.x} ${target.y}`;
  }

  // If target is to the left of source, route around
  if (dx < 60) {
    const offset = 40;
    const sourceOffsetX = source.x + offset;
    const targetOffsetX = target.x - offset;
    const midY = source.y + dy / 2;

    return generateOrthogonalPath(
      source,
      { x: sourceOffsetX, y: source.y },
      { x: sourceOffsetX, y: midY },
      { x: targetOffsetX, y: midY },
      { x: targetOffsetX, y: target.y },
      target,
      cornerRadius
    );
  }

  // Standard case: go right, turn, go to target height, turn, go to target
  const vertDir = dy > 0 ? 1 : -1; // 1 = down, -1 = up
  const r = Math.min(cornerRadius, Math.abs(dy) / 2, Math.abs(dx - minHorizontal * 2) / 2);

  if (r < 2) {
    // Not enough room for rounded corners, fall back to straight segments
    return `M ${source.x} ${source.y} L ${midX} ${source.y} L ${midX} ${target.y} L ${target.x} ${target.y}`;
  }

  // Build path with rounded corners at the two turns
  // First turn: at (midX, source.y), going from horizontal to vertical
  // Second turn: at (midX, target.y), going from vertical to horizontal

  const path = [
    `M ${source.x} ${source.y}`,
    // Horizontal line to first corner
    `L ${midX - r} ${source.y}`,
    // First rounded corner (horizontal to vertical)
    `Q ${midX} ${source.y} ${midX} ${source.y + r * vertDir}`,
    // Vertical line
    `L ${midX} ${target.y - r * vertDir}`,
    // Second rounded corner (vertical to horizontal)
    `Q ${midX} ${target.y} ${midX + r} ${target.y}`,
    // Horizontal line to target
    `L ${target.x} ${target.y}`
  ];

  return path.join(' ');
}

/**
 * Generate path for complex routing with multiple waypoints
 */
function generateOrthogonalPath(p1, p2, p3, p4, p5, p6, r) {
  const radius = Math.min(r, 6);

  const path = [
    `M ${p1.x} ${p1.y}`,
    `L ${p2.x} ${p2.y}`,
    roundedCorner(p2, p3, radius),
    `L ${p3.x} ${p3.y}`,
    roundedCorner(p3, p4, radius),
    `L ${p4.x} ${p4.y}`,
    roundedCorner(p4, p5, radius),
    `L ${p5.x} ${p5.y}`,
    roundedCorner(p5, p6, radius),
    `L ${p6.x} ${p6.y}`
  ];

  return path.filter(Boolean).join(' ');
}

/**
 * Generate a quadratic curve for a rounded corner between two line segments
 */
function roundedCorner(from, to, r) {
  if (r < 1) return '';
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return '';

  // Direction vectors
  const dirX = dx === 0 ? 0 : dx > 0 ? 1 : -1;
  const dirY = dy === 0 ? 0 : dy > 0 ? 1 : -1;

  return `Q ${from.x} ${from.y} ${from.x + dirX * r} ${from.y + dirY * r}`;
}

/**
 * Calculate midpoint of the path for label placement
 */
function getMidpoint(source, target) {
  return {
    x: (source.x + target.x) / 2,
    y: (source.y + target.y) / 2
  };
}

/**
 * ArchitectureLink Component
 *
 * Renders a connection line between two nodes in the architecture diagram.
 * Supports three connection types: DATA_FLOW, TRUST_CONTROL, INTEGRATION
 */
const ArchitectureLink = memo(function ArchitectureLink({
  edge,
  sourceNode,
  targetNode,
  isSelected,
  onSelect,
  onDelete
}) {
  const { id, type, label, animated } = edge;

  // Get connection type configuration
  const connectionConfig = CONNECTION_TYPES[type] || CONNECTION_TYPES.DATA_FLOW;
  const { color, style: lineStyle, hasArrow } = connectionConfig;

  // Calculate anchor points
  const source = useMemo(() =>
    sourceNode ? getNodeAnchor(sourceNode, 'right') : { x: 0, y: 0 },
    [sourceNode]
  );

  const target = useMemo(() =>
    targetNode ? getNodeAnchor(targetNode, 'left') : { x: 0, y: 0 },
    [targetNode]
  );

  // Generate path
  const path = useMemo(() =>
    generatePath(source, target),
    [source, target]
  );

  // Calculate label position
  const labelPos = useMemo(() =>
    getMidpoint(source, target),
    [source, target]
  );

  // Don't render if nodes are missing
  if (!sourceNode || !targetNode) return null;

  // Unique marker ID for this edge's arrow
  const markerId = `arrow-${id}`;

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect?.(id);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(id);
  };

  return (
    <g className={`arch-link ${isSelected ? 'arch-link--selected' : ''}`}>
      {/* Arrow marker definition */}
      {hasArrow && (
        <defs>
          <marker
            id={markerId}
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path
              d="M0,0 L0,6 L9,3 z"
              fill={isSelected ? 'var(--color-primary, #5b8def)' : color}
            />
          </marker>
        </defs>
      )}

      {/* Invisible wider path for easier clicking */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth="20"
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      />

      {/* Visible path */}
      <path
        d={path}
        fill="none"
        stroke={isSelected ? 'var(--color-primary, #5b8def)' : color}
        strokeWidth={isSelected ? 3 : 2}
        strokeDasharray={lineStyle === 'dashed' ? '6 4' : 'none'}
        markerEnd={hasArrow ? `url(#${markerId})` : undefined}
        className={animated ? 'arch-link__path--animated' : ''}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      />

      {/* Label */}
      {label && (
        <g transform={`translate(${labelPos.x}, ${labelPos.y})`}>
          {/* Label background */}
          <rect
            x={-label.length * 3.5 - 6}
            y={-10}
            width={label.length * 7 + 12}
            height={20}
            rx={4}
            fill="var(--bg-secondary, #0f1421)"
            stroke={isSelected ? 'var(--color-primary, #5b8def)' : 'var(--border-color, #2d3548)'}
            strokeWidth={1}
            onClick={handleClick}
            style={{ cursor: 'pointer' }}
          />
          {/* Label text */}
          <text
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--text-secondary, #94a3b8)"
            fontSize="11"
            fontFamily="inherit"
            onClick={handleClick}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {label}
          </text>
        </g>
      )}

      {/* Delete button when selected */}
      {isSelected && (
        <g transform={`translate(${labelPos.x + 40}, ${labelPos.y})`}>
          <circle
            r="10"
            fill="var(--bg-secondary, #0f1421)"
            stroke="var(--color-primary, #5b8def)"
            strokeWidth={1}
            onClick={handleDelete}
            style={{ cursor: 'pointer' }}
          />
          <text
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--color-primary, #5b8def)"
            fontSize="14"
            fontWeight="bold"
            onClick={handleDelete}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            ×
          </text>
        </g>
      )}
    </g>
  );
});

export default ArchitectureLink;
